import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { AdminIdentity, requestAdminAccess } from "utils/adminAccessApi";

interface RequestAccessPageProps {
  identity: AdminIdentity;
}

type FormState = "idle" | "submitting" | "submitted" | "error";

function RequestAccessPage({ identity }: RequestAccessPageProps) {
  const [formIdentity, setFormIdentity] = useState<AdminIdentity>(identity);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setReasonError("Please provide a reason for requesting access.");
      return;
    }
    setReasonError("");
    setFormState("submitting");

    try {
      const result = await requestAdminAccess(formIdentity, reason.trim());
      if (result.requestSubmitted || result.status === "ALREADY_APPROVED") {
        setFormState("submitted");
      } else {
        setErrorMessage(result.message || "Failed to submit request. Please try again.");
        setFormState("error");
      }
    } catch {
      setErrorMessage("An unexpected error occurred. Please try again later.");
      setFormState("error");
    }
  };

  if (formState === "submitted") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f7fa",
          px: 2,
        }}
      >
        <Stack
          spacing={3}
          alignItems="center"
          sx={{
            maxWidth: 480,
            width: "100%",
            p: 4,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            textAlign: "center",
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "success.main" }} />
          <Typography variant="h5" fontWeight={700} color="secondary.main">
            Request Submitted
          </Typography>
          <Typography color="text.secondary">
            Your access request has been submitted. Please contact your admin for
            approval.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fa",
        px: 2,
      }}
    >
      <Stack
        spacing={3}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Stack spacing={1} alignItems="center" textAlign="center">
          <LockOutlinedIcon sx={{ fontSize: 48, color: "secondary.main", mb: 1 }} />
          <Typography variant="h5" fontWeight={700} color="secondary.main">
            Access Required
          </Typography>
          <Typography color="text.secondary">
            You don&apos;t have access to the Verified KYC Admin portal. Submit a
            request below and your admin will review it.
          </Typography>
        </Stack>

        {formState === "error" && (
          <Alert severity="error" onClose={() => setFormState("idle")}>
            {errorMessage}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Name"
            value={formIdentity.userName}
            onChange={(e) =>
              setFormIdentity((prev) => ({ ...prev, userName: e.target.value }))
            }
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            value={formIdentity.emailId}
            onChange={(e) =>
              setFormIdentity((prev) => ({ ...prev, emailId: e.target.value }))
            }
            fullWidth
            size="small"
          />
          <TextField
            label="CWID"
            value={formIdentity.cwid}
            onChange={(e) =>
              setFormIdentity((prev) => ({ ...prev, cwid: e.target.value }))
            }
            fullWidth
            size="small"
          />
          <TextField
            label="Reason for access"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonError("");
            }}
            multiline
            rows={3}
            fullWidth
            size="small"
            error={!!reasonError}
            helperText={reasonError}
            placeholder="Briefly describe why you need access to this portal"
          />
        </Stack>

        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={formState === "submitting"}
          sx={{ bgcolor: "#007CBF", "&:hover": { bgcolor: "#006aa3" } }}
        >
          {formState === "submitting" ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Request Access"
          )}
        </Button>
      </Stack>
    </Box>
  );
}

export default RequestAccessPage;
